(ns common.schema.entities.permission-checks
  "Read and delete permission checks that more than one entity-type definition uses.

   These functions were in `common.schema.entity-types`. A definition in the common
   registry cannot require that namespace, because it requires the registry. A
   cycle would result. Each function here has the same body as before, and
   `common.schema.entity-types` keeps its old names for them.

   The `oppo-read-check-fn` here is the older check. It has no plan check. The
   `common.schema.entities.oppo.permissions` namespace has the newer check for the
   oppo entity type. Do not replace one with the other without a decision about
   the decisions, the eSign processes and the counterparty diligences that call
   this one."
  (:require
   [common.features.plan :as plan]
   [common.roles :as roles]
   [common.transaction-process.access :as tp-access]))

(defn created-by-account?
  [entity account]
  (let [created-by (:common/created-by entity)
        created-by-id (or (:db/id created-by)
                          (when (integer? created-by)
                            created-by))]
    (= created-by-id (:db/id account))))

(defn write-permissions-to-related-entity?
  [entity has-write-permissions?]
  (->> entity
       :common/related-entities
       (some has-write-permissions?)))

(defn delete-not-allowed!
  [{:keys [tr]}]
  (tr :not-allowed))

(def activity-delete-check-fn
  (fn [{:keys [tr account role entity has-write-permissions?]}]
    (when-not (or (roles/admin? role)
                  (created-by-account? entity account)
                  (and (roles/user? role)
                       (write-permissions-to-related-entity? entity has-write-permissions?)))
      (tr :not-allowed))))

(def risk-delete-check-fn
  (fn [{:keys [tr account role entity has-write-permissions?]}]
    (when-not (or (roles/admin? role)
                  (created-by-account? entity account)
                  (and (roles/user? role)
                       (write-permissions-to-related-entity? entity has-write-permissions?)))
      (tr :not-allowed))))

(def oppo-read-check-fn
  (fn [{:keys [account-current-permissions role target entity]}]
    (or (and (= role :role/guest-buy-side)
             target
             (= (:db/id entity) (:db/id target)))
        (and (contains? (:account-modules account-current-permissions)
                        :module/dealflow)
             (contains? (:account-access-to-pipelines account-current-permissions)
                        (->> entity
                             :oppo/pipeline
                             :db/id))))))

(def exit-oppo-read-check-fn
  (fn [{:keys [account-current-permissions entity] :as _ctx}]
    (and (contains? (:account-modules account-current-permissions)
                    :module/exit-process-management)
         (contains? (:account-access-to-pipelines account-current-permissions)
                    (->> entity
                         :exit-opportunity/pipeline
                         :db/id)))))

(defn decisions-access?
  [{:keys [tenant account-current-permissions] :as _ctx}]
  (and (plan/available :feature/decisions tenant)
       (contains? (:account-modules account-current-permissions)
                  :module/decisions)))

(def decision-read-check-fn
  (fn [{:keys [entity tenant] :as ctx}]
    (let [tenant-id (:db/id tenant)
          containing-entity (:common/containing-entity entity)
          containing-entity-type (:entity/type containing-entity)
          tp (tp-access/find-parent-transaction-process entity)]
      (when (-> entity :common/tenant :db/id (= tenant-id))
        (cond
          tp
          (tp-access/can-access-transaction-process? (assoc ctx :entity tp))

          (and containing-entity
               (-> containing-entity :common/disabled? not)
               (decisions-access? ctx))
          (case containing-entity-type
            :entity.type/oppo
            (oppo-read-check-fn (assoc ctx :entity containing-entity))

            :entity.type/exit-opportunity
            (exit-oppo-read-check-fn (assoc ctx :entity containing-entity))

            ;; TODO: add others when applicable
            false))))))

(def entities-with-decisions
  #{:entity.type/oppo
    :entity.type/exit-opportunity})

(defn decision-disable-permissions-check-fn
  [{:keys [tr entity tenant has-write-permissions?] :as ctx}]
  (let [tenant-id (:db/id tenant)
        containing-entity (:common/containing-entity entity)
        containing-entity-type (:entity/type containing-entity)
        tp (tp-access/find-parent-transaction-process entity)]
    (when-not (and (-> entity :common/tenant :db/id (= tenant-id))
                   (cond
                     tp
                     (tp-access/can-access-transaction-process? (assoc ctx :entity tp))

                     :else
                     (and (decisions-access? ctx)
                          (contains? entities-with-decisions
                                     containing-entity-type)

                          ;; TODO: we need explicit delete permissions to be configured for non activity entity types
                          (has-write-permissions? containing-entity))))
      (tr :not-allowed))))

(defn counterparty-diligence-process-read-check-fn
  [{:keys [tenant role tenant-plan account-current-permissions]}]
  (and (plan/available :feature/counterparty-diligence tenant role tenant-plan)
       (or (roles/admin? role)
           (contains? (:account-modules account-current-permissions)
                      :module/counterparty-diligence-admin))))

(def esign-process-read-check-fn
  (fn [{:keys [tenant entity account-current-permissions] :as ctx}]
    (let [tenant-id (:db/id tenant)
          target (:esign-process/target entity)]
      (when (and (plan/available :feature/entity-esign tenant)
                 (-> target
                     :common/disabled?
                     not)
                 (-> target
                     :common/tenant
                     :db/id
                     (= tenant-id)))
        (case (:entity/type target)
          ;; TODO: Should we not check kyc access?
          :entity.type/kyc-process true

          :entity.type/oppo
          (let [oppo-ctx (assoc ctx :entity target)]
            (oppo-read-check-fn oppo-ctx))

          :entity.type/exit-opportunity
          (let [ctx (assoc ctx :entity target)]
            (exit-oppo-read-check-fn ctx))

          :entity.type/fundraising-opportunity
          (and (contains? (:account-modules account-current-permissions)
                          :module/fundraising)
               (contains? (:account-access-to-pipelines account-current-permissions)
                          (->> target
                               :fundraising-opportunity/pipeline
                               :db/id)))

          :entity.type/sales-opportunity
          (and (contains? (:account-modules account-current-permissions)
                          :module/sales-pipelines)
               (contains? (:account-access-to-pipelines account-current-permissions)
                          (->> target
                               :sales-opportunity/pipeline
                               :db/id)))

          ;; TODO: This was copied from sale agreement access check, is this enough?
          :entity.type/sale-agreement
          (contains? (:account-modules account-current-permissions)
                     :module/sales-pipelines)

          :entity.type/counterparty-diligence-process
          (let [ctx (assoc ctx :entity target)]
            (counterparty-diligence-process-read-check-fn ctx)))))))
